import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  HardDrive,
  LoaderCircle,
  RefreshCw,
  Save,
  ScanSearch,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import {
  formatRecordingBytes,
  retentionFormFromStatus,
  retentionFormPayload,
  retentionPresentation,
  type RecordingRetentionForm,
} from '../RecordingRetentionState';
import { telephonyApi } from '../telephonyApi';
import type { RecordingMaintenanceJob } from '../types';

function dateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function jobType(value: string) {
  return value === 'CLEANUP' ? 'Очистка' : 'Проверка';
}

function jobStatus(value: string) {
  return ({ PENDING: 'В очереди', RUNNING: 'Выполняется', COMPLETED: 'Завершено', FAILED: 'Ошибка' } as Record<string, string>)[value] || value;
}

function storageLevel(value: string) {
  return ({ OK: 'Норма', WARNING: 'Внимание', CRITICAL: 'Критично', UNKNOWN: 'Нет данных' } as Record<string, string>)[value] || 'Нет данных';
}

export function TelephonyRecordingRetentionSettings() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<RecordingRetentionForm | null>(null);
  const [cleanupConfirmation, setCleanupConfirmation] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const retentionQuery = useQuery({
    queryKey: ['telephony-recording-retention'],
    queryFn: telephonyApi.retentionStatus,
    refetchInterval: query => query.state.data?.maintenance.active ? 3_000 : 15_000,
    retry: false,
  });

  useEffect(() => {
    if (!retentionQuery.data) return;
    setForm(current => current || retentionFormFromStatus(retentionQuery.data));
  }, [retentionQuery.data]);

  async function refreshRelated() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['telephony-recording-retention'] }),
      queryClient.invalidateQueries({ queryKey: ['telephony-browser-diagnostics'] }),
    ]);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!form || !retentionQuery.data) throw new Error('Настройки ещё не загружены');
      const result = retentionFormPayload(form, retentionQuery.data.settings.mode);
      if (!result.payload) throw new Error(result.error);
      return telephonyApi.updateRetention(result.payload);
    },
    onSuccess: async value => {
      queryClient.setQueryData(['telephony-recording-retention'], value);
      setForm(retentionFormFromStatus(value));
      setMessage(value.settings.mode === 'ENABLED' ? 'Политика сохранена, очистка включена' : 'Политика сохранена в безопасном режиме отчёта');
      setError('');
      await refreshRelated();
    },
    onError: reason => {
      setMessage('');
      setError(reason instanceof Error ? reason.message : 'Не удалось сохранить настройки хранения');
    },
  });

  const scanMutation = useMutation({
    mutationFn: telephonyApi.scanRetention,
    onSuccess: async () => {
      setMessage('Проверка поставлена в очередь');
      setError('');
      await refreshRelated();
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось запустить проверку'),
  });

  const cleanupMutation = useMutation({
    mutationFn: () => telephonyApi.runRetention(cleanupConfirmation),
    onSuccess: async () => {
      setCleanupConfirmation('');
      setMessage('Очистка поставлена в очередь');
      setError('');
      await refreshRelated();
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось запустить очистку'),
  });

  const status = retentionQuery.data;
  const presentation = useMemo(() => status ? retentionPresentation(status) : null, [status]);
  const pending = saveMutation.isPending || scanMutation.isPending || cleanupMutation.isPending;
  const active = status?.maintenance.active;

  if (retentionQuery.isError) {
    return <div className="form-error"><AlertTriangle /> Не удалось загрузить настройки хранения записей.</div>;
  }
  if (retentionQuery.isLoading || !status || !form || !presentation) {
    return <div className="recording-retention-loading"><LoaderCircle className="spinning" /> Загружаем состояние хранилища…</div>;
  }

  const update = <K extends keyof RecordingRetentionForm,>(key: K, value: RecordingRetentionForm[K]) => {
    setForm(current => current ? { ...current, [key]: value } : current);
  };
  const jobs: RecordingMaintenanceJob[] = status.maintenance.runs || [];

  return <div className="recording-retention-module">
    <section className={`recording-retention-banner ${presentation.modeTone}`}>
      <span>{status.settings.mode === 'ENABLED' ? <Trash2 /> : <ShieldCheck />}</span>
      <div>
        <small>РЕЖИМ ХРАНЕНИЯ</small>
        <h3>{presentation.modeLabel}</h3>
        <p>{status.settings.mode === 'ENABLED'
          ? `Записи старше ${status.settings.retentionDays} дней удаляются отдельным сервисом. Важные разговоры защищены.`
          : 'Сервис только считает кандидатов и контролирует диск. Файлы не удаляются.'}</p>
      </div>
      <em>{active ? jobStatus(active.status) : 'Нет активной задачи'}</em>
    </section>

    <div className="recording-retention-summary">
      <article><span><Database /></span><div><small>ЗАНЯТО ЗАПИСЯМИ</small><b>{presentation.managedBytesLabel}</b><em>{status.recordings.available} доступно</em></div></article>
      <article><span><ShieldCheck /></span><div><small>ЗАЩИЩЕНО</small><b>{status.recordings.important}</b><em>важных разговоров</em></div></article>
      <article className={presentation.storageTone}><span><HardDrive /></span><div><small>ХРАНИЛИЩЕ · {presentation.storageLevelLabel}</small><b>{presentation.usedPercentLabel}</b><em>свободно {presentation.freeBytesLabel}</em></div></article>
      <article><span><ScanSearch /></span><div><small>КАНДИДАТЫ</small><b>{status.recordings.candidateCount}</b><em>{formatRecordingBytes(status.recordings.candidateBytes)}</em></div></article>
    </div>

    {error && <div className="form-error"><AlertTriangle /> {error}</div>}
    {message && <div className="form-success"><CheckCircle2 /> {message}</div>}

    <form className="recording-retention-form" onSubmit={(event: FormEvent) => { event.preventDefault(); saveMutation.mutate(); }}>
      <div className="recording-retention-section-head">
        <div><small>ПОЛИТИКА</small><h3>Сроки и ограничения</h3><p>Изменения применяются к следующим циклам обслуживания и не влияют на активные звонки.</p></div>
      </div>
      <div className="recording-retention-grid">
        <label>Режим<select value={form.mode} onChange={event => update('mode', event.target.value as RecordingRetentionForm['mode'])}><option value="REPORT_ONLY">Только отчёт — без удаления</option><option value="ENABLED">Очистка включена</option></select></label>
        <label>Хранить записи, дней<input type="number" min={30} max={3650} value={form.retentionDays} onChange={event => update('retentionDays', event.target.value)} /></label>
        <label>Файлов за один цикл<input type="number" min={1} max={500} value={form.batchSize} onChange={event => update('batchSize', event.target.value)} /></label>
        <label>Интервал проверки, минут<input type="number" min={15} max={1440} value={form.runIntervalMinutes} onChange={event => update('runIntervalMinutes', event.target.value)} /></label>
        <label>Предупреждение при заполнении, %<input type="number" min={50} max={95} value={form.warningUsedPercent} onChange={event => update('warningUsedPercent', event.target.value)} /></label>
        <label>Критический уровень, %<input type="number" min={51} max={99} value={form.criticalUsedPercent} onChange={event => update('criticalUsedPercent', event.target.value)} /></label>
        <label>Предупреждение при остатке, ГиБ<input inputMode="decimal" value={form.warningFreeGiB} onChange={event => update('warningFreeGiB', event.target.value)} /></label>
        <label>Критический остаток, ГиБ<input inputMode="decimal" value={form.criticalFreeGiB} onChange={event => update('criticalFreeGiB', event.target.value)} /></label>
      </div>
      {status.settings.mode !== 'ENABLED' && form.mode === 'ENABLED' && <div className="recording-retention-confirm danger">
        <AlertTriangle />
        <div><b>Вы включаете физическое удаление WAV</b><small>Для подтверждения введите УДАЛЯТЬ. Важные записи, активные звонки и неизвестные файлы удаляться не будут.</small><input value={form.confirmation} onChange={event => update('confirmation', event.target.value)} placeholder="УДАЛЯТЬ" autoComplete="off" /></div>
      </div>}
      <div className="form-actions"><button className="button primary" disabled={pending}><Save /> Сохранить политику</button></div>
    </form>

    <section className="recording-retention-operations">
      <div className="recording-retention-section-head"><div><small>ОБСЛУЖИВАНИЕ</small><h3>Проверка и ручной запуск</h3><p>Проверка безопасна при любом режиме. Очистка доступна только после включения политики.</p></div></div>
      <div className="recording-retention-operation-grid">
        <article><span><ScanSearch /></span><div><b>Пересчитать хранилище</b><small>Обновить объём, кандидатов, неизвестные и отсутствующие файлы.</small></div><button type="button" className="button ghost" disabled={pending || Boolean(active)} onClick={() => scanMutation.mutate()}><RefreshCw /> Проверить</button></article>
        <article className="danger"><span><Trash2 /></span><div><b>Запустить очистку сейчас</b><small>Будет обработано не больше {status.settings.batchSize} подтверждённых кандидатов.</small><input value={cleanupConfirmation} onChange={event => setCleanupConfirmation(event.target.value)} disabled={status.settings.mode !== 'ENABLED'} placeholder="УДАЛЯТЬ" autoComplete="off" /></div><button type="button" className="button danger" disabled={pending || Boolean(active) || status.settings.mode !== 'ENABLED' || cleanupConfirmation !== 'УДАЛЯТЬ'} onClick={() => cleanupMutation.mutate()}><Trash2 /> Очистить</button></article>
      </div>
    </section>

    <section className="recording-retention-runs">
      <div className="recording-retention-section-head"><div><small>ЖУРНАЛ</small><h3>Последние запуски</h3><p>До 20 последних проверок и очисток с результатом без внутренних путей сервера.</p></div><button type="button" className="icon-button" aria-label="Обновить журнал" onClick={() => void retentionQuery.refetch()} disabled={retentionQuery.isFetching}><RefreshCw className={retentionQuery.isFetching ? 'spinning' : ''} /></button></div>
      {!jobs.length ? <div className="recording-retention-empty">Запуски ещё не выполнялись.</div> : <div className="recording-retention-run-table">
        <div className="head"><span>Запуск</span><span>Статус</span><span>Кандидаты</span><span>Удалено</span><span>Хранилище</span><span>Результат</span></div>
        {jobs.map(job => <div className="row" key={job.id}>
          <span><b>{jobType(job.jobType)}</b><small>{dateTime(job.startedAt || job.createdAt)}</small></span>
          <span><em className={`status-${job.status.toLowerCase()}`}>{jobStatus(job.status)}</em></span>
          <span><b>{job.candidateCount}</b><small>{formatRecordingBytes(job.candidateBytes)}</small></span>
          <span><b>{job.deletedFiles}</b><small>{formatRecordingBytes(job.deletedBytes)}</small></span>
          <span><b>{storageLevel(job.storageLevel)}</b><small>{formatRecordingBytes(job.managedBytes)} записей</small></span>
          <span><b>{job.message || '—'}</b>{(job.unknownFiles > 0 || job.missingFiles > 0 || job.failedFiles > 0) && <small>Неизвестных: {job.unknownFiles} · отсутствует: {job.missingFiles} · ошибок: {job.failedFiles}</small>}{job.lastError && <small className="error">{job.lastError}</small>}</span>
        </div>)}
      </div>}
    </section>
  </div>;
}
